
import Vue from 'vue'
import { Project } from '../../../../../types/main'
import { CONNECTOR_TYPES, CHANNEL_TYPES, PROVIDER_TYPES } from '../../../../../vars/general'
import CustomActivatorSelect from '../../../../inputs/CustomActivatorSelect.vue'
import BaseInputOutlined from '../../../../inputs/BaseInputOutlined.vue'
import { getCascadesByProject } from '@/api/networkFixer'

export default Vue.extend({
    name: 'ChannelStep',
    components: {
        CustomActivatorSelect,
        BaseInputOutlined,
    },
    props: {
        value: { type: Object },
        app: { type: Object },
    },
    data: () => ({
        cascades: [] as any[],
    }),
    computed: {
        project(): Project {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id)
        },
        // Получаем список каналов отправки из проекта
        channels(): any[] {
            const project = this.project
            if (!project?.isChannelsRework) return []
            
            const channelTypes = Object.values(CHANNEL_TYPES)
            return project.connectors.filter(c => 
                !c.isChisai && 
                c.connectorType !== CONNECTOR_TYPES.whatsappBasis && // Исключаем whatsappBasis
                channelTypes.includes(c.connectorType) &&
                // Не показываем WhatsApp-каналы с провайдером DH
                !this.isWhatsappDhChannel(c)
            )
        },
        // Группы каналов для нумерации
        channelGroups() {
            return [
                {
                    name: 'whatsapp',
                    channelTypes: [CHANNEL_TYPES.whatsappChannel],
                },
                {
                    name: 'telegram',
                    channelTypes: [CHANNEL_TYPES.telegramChannel],
                },
                {
                    name: 'max',
                    channelTypes: [CHANNEL_TYPES.maxChannel],
                },
            ]
        },
        // Получаем индекс канала в группе
        getChannelIndex() {
            return (connectorType: string, connectorId: string) => {
                const group = this.channelGroups.find(g => g.channelTypes.includes(connectorType))
                if (!group) return 1

                // ВАЖНО: индексируем как в таблице каналов (ConnectorsListView):
                // считаем по всем коннекторам группы, включая DH, чтобы имена "WhatsApp N" не съезжали
                const channelsInGroup = (this.project?.connectors || [])
                    .filter(c => !c.isChisai && c.connectorType === connectorType)
                    .sort((a, b) => {
                        const aTime = a.createdAt && a.createdAt > 0 ? a.createdAt : Number.MAX_SAFE_INTEGER
                        const bTime = b.createdAt && b.createdAt > 0 ? b.createdAt : Number.MAX_SAFE_INTEGER
                        if (aTime !== Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) {
                            return aTime - bTime
                        }
                        if (aTime === Number.MAX_SAFE_INTEGER && bTime !== Number.MAX_SAFE_INTEGER) return 1
                        if (aTime !== Number.MAX_SAFE_INTEGER && bTime === Number.MAX_SAFE_INTEGER) return -1
                        return a.connectorId.localeCompare(b.connectorId)
                    })

                const currentIndex = channelsInGroup.findIndex(c => c.connectorId === connectorId)
                return currentIndex >= 0 ? currentIndex + 1 : 1
            }
        },
        // Получаем отображаемое имя канала
        getChannelDisplayName() {
            return (connectorType: string, index: number) => {
                const baseNames = {
                    [CHANNEL_TYPES.whatsappChannel]: 'WhatsApp',
                    [CHANNEL_TYPES.telegramChannel]: 'Telegram',
                    [CHANNEL_TYPES.maxChannel]: 'MAX',
                }
                const baseName = baseNames[connectorType] || connectorType
                
                if (index === 1) {
                    return baseName
                }
                return `${baseName} ${index}`
            }
        },
        // Получаем название канала по умолчанию из общих настроек
        defaultChannelName(): string | null {
            if (!this.app?.defaultChannel) {
                return null
            }
            
            const defaultChannel = this.app.defaultChannel
            const channelId = defaultChannel.id

            // Если по умолчанию выбран каскад
            if (defaultChannel.type === 'cascade') {
                const cascade = this.cascades.find(c => String(c.id) === String(channelId))
                if (cascade) {
                    return cascade.cascadeType === 'custom'
                        ? this.getCustomCascadeLabel(cascade)
                        : 'Каскад'
                }
                return 'Каскад'
            }
            
            // Ищем канал в списке каналов
            const channel = this.channels.find(c => {
                const cId = c.channelId || c.connectorId
                return String(cId) === String(channelId)
            })
            
            if (channel) {
                const connectorType = channel.connectorType
                const index = this.getChannelIndex(connectorType, channel.connectorId)
                // Для пункта "По умолчанию" показываем название канала и номер, к которому он подключен
                const displayName = this.getChannelDisplayName(connectorType, index)
                const phone = (channel as any).phone
                return phone ? `${displayName} (${phone})` : displayName
            }
            
            return null
        },
        // Опции для выбора канала
        channelOptions(): any[] {
            const options = []
            
            // Добавляем опцию "По умолчанию" в начало
            const defaultName = this.defaultChannelName
            const defaultLabel = defaultName ? `По умолчанию (${defaultName})` : 'По умолчанию'
            options.push({
                label: defaultLabel,
                value: null
            })

            // Добавляем остальные каналы
            this.channels.forEach(connector => {
                // Не показываем WhatsApp-каналы с провайдером DH (даже если они каким-то образом попали в this.channels)
                if (this.isWhatsappDhChannel(connector)) {
                    return
                }
                const connectorType = connector.connectorType
                const index = this.getChannelIndex(connectorType, connector.connectorId)
                const displayName = this.getChannelDisplayName(connectorType, index)
                const channelId = connector.channelId || connector.connectorId
                const phone = (connector as any).phone
                const label = phone ? `${displayName} (${phone})` : displayName
                
                options.push({
                    label,
                    value: JSON.stringify({ type: 'channel', id: channelId })
                })
            })

            // Добавляем каскады (notifications + custom)
            const cascadesForOptions = this.cascades
                .filter(c => c && (c.cascadeType === 'notifications' || c.cascadeType === 'custom'))
                .filter(c => c.cascadeType !== 'custom' || (Array.isArray(c.channels) && c.channels.length > 0))
            const sortedCustomCascades = this.sortCustomCascadesByCreatedAt(
                cascadesForOptions.filter(c => c.cascadeType === 'custom'),
            )
            const sortedCascadesForOptions = [
                ...cascadesForOptions.filter(c => c.cascadeType !== 'custom'),
                ...sortedCustomCascades,
            ]
            sortedCascadesForOptions.forEach(c => {
                options.push({
                    label: c.cascadeType === 'custom' ? this.getCustomCascadeLabel(c) : 'Каскад',
                    value: JSON.stringify({ type: 'cascade', id: c.id }),
                })
            })
            
            return options
        },
        // Выбранный канал
        selectedChannel(): string | null {
            // Если channel не установлен, undefined или null, значит выбран "По умолчанию"
            if (!this.value || !this.value.channel || this.value.channel === null || this.value.channel === undefined) {
                return null
            }
            
            const channel = this.value.channel
            // Проверяем, что channel является объектом с id
            if (!channel || typeof channel !== 'object' || !channel.id) {
                return null
            }
            
            const channelId = channel.id
            const options = this.channelOptions
            
            // Ищем соответствующее значение в channelOptions по id
            const matchingOption = options.find(option => {
                if (!option.value) return false
                try {
                    const optionData = JSON.parse(option.value)
                    // Сравниваем id (может быть строкой или числом)
                    return String(optionData.id) === String(channelId)
                } catch (e) {
                    return false
                }
            })
            
            // Если нашли совпадение, возвращаем его value
            if (matchingOption) {
                return matchingOption.value
            }
            
            // Если не нашли, пытаемся создать значение из сохраненных данных
            return JSON.stringify(channel)
        },
    },
    async mounted() {
        try {
            const projectId = this.$router.currentRoute.params.id
            const { data, error } = await getCascadesByProject(projectId)
            if (!error && data) {
                this.cascades = data
            } else {
                this.cascades = []
            }
        } catch (e) {
            console.error('Ошибка загрузки каскадов:', String(e))
            this.cascades = []
        }
    },
    methods: {
        getCustomCascadeCreatedAtTimestamp(cascade: any) {
            const ts = cascade?.createdAt ? new Date(cascade.createdAt).getTime() : Number.MAX_SAFE_INTEGER
            return Number.isNaN(ts) ? Number.MAX_SAFE_INTEGER : ts
        },
        sortCustomCascadesByCreatedAt(cascades: any[]) {
            return [...(cascades || [])].sort((a: any, b: any) => {
                const aTime = this.getCustomCascadeCreatedAtTimestamp(a)
                const bTime = this.getCustomCascadeCreatedAtTimestamp(b)
                if (aTime !== bTime) return aTime - bTime
                return String(a?.id ?? '').localeCompare(String(b?.id ?? ''))
            })
        },
        getCustomCascadeNumber(cascadeId: string | number) {
            const customCascades = this.sortCustomCascadesByCreatedAt(
                this.cascades.filter(c => c && c.cascadeType === 'custom'),
            )
            const customIndex = customCascades.findIndex(c => String(c.id) === String(cascadeId))
            return customIndex >= 0 ? customIndex + 2 : null
        },
        getCustomCascadeLabel(cascade: any) {
            const number = this.getCustomCascadeNumber(cascade?.id)
            const name = cascade?.name ? String(cascade.name).trim() : ''
            if (!number) return 'Каскад'
            return name ? `Каскад ${number} - ${name}` : `Каскад ${number}`
        },
        isWhatsappDhChannel(c: any) {
            // Проверяем, является ли канал WhatsApp с провайдером DH (providerType === 'whatsapp')
            if (c?.providerType === PROVIDER_TYPES.whatsapp) return true
            if (c?.connector?.providerType === PROVIDER_TYPES.whatsapp) return true
            try {
                // fallback по channelId: ищем в проекте коннектор с таким channelId и providerType=whatsapp
                const cid = c?.channelId
                if (cid) {
                    const found = (this.project?.connectors || []).find(
                        (x: any) => String(x?.channelId) === String(cid),
                    )
                    if (found?.providerType === PROVIDER_TYPES.whatsapp) return true
                }
            } catch (e) {
                // ignore
            }
            try {
                const getter = (this.$store.getters as any)['connectorById']
                const full = typeof getter === 'function' ? getter(c?.connectorId || c?.id) : null
                if (full?.providerType === PROVIDER_TYPES.whatsapp) return true
            } catch (e) {
                // ignore
            }
            return false
        },
        // Обновление канала
        updateChannel(value: string | null) {
            if (value === null) {
                this.$emit('input', { ...this.value, channel: null })
            } else {
                try {
                    const channelData = JSON.parse(value)
                    this.$emit('input', { ...this.value, channel: channelData })
                } catch (e) {
                    console.error('Ошибка при парсинге канала:', e)
                    this.$emit('input', { ...this.value, channel: null })
                }
            }
        },
    },
})
